import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Heart, MapPin, ChevronRight, ClipboardCheck, Recycle, Sparkles, HelpCircle, Users, Scale } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';
import SEO from '../../components/SEO';

const DebarrasSuccessionAnnecy = () => {
  const quartiers = [
    {
      nom: 'La Prairie et Courier',
      description: "Quartier prisé des familles annéciennes depuis l'après-guerre, entre le Paquier et les rives du lac. On y trouve des maisons bourgeoises transmises de génération en génération, souvent meublées à l'identique depuis les années 60. Les successions y impliquent régulièrement le tri de mobilier ancien, de vaisselle de famille et de bibliothèques entières accumulées sur plusieurs décennies."
    },
    {
      nom: 'Les Balmettes et la Préfecture',
      description: "Secteur résidentiel cossu à proximité immédiate des études notariales du centre-ville. Les appartements haussmanniens de ce quartier, souvent spacieux (T4-T5), sont fréquemment concernés par des successions complexes avec plusieurs héritiers. Le volume de mobilier lourd — buffets, armoires savoyardes, pianos — y nécessite une logistique de portage spécifique dans les étages."
    },
    {
      nom: 'La Vieille Ville et le Thiou',
      description: "Immeubles historiques du secteur sauvegardé, habités parfois par les mêmes locataires ou propriétaires durant 30 ou 40 ans. Les logements renferment des intérieurs restés intacts : meubles encastrés, bibelots, vêtements, papiers administratifs. L'accès par les ruelles pavées et les escaliers en colimaçon impose un portage manuel délicat que notre équipe maîtrise au quotidien."
    },
    {
      nom: 'Cran-Gevrier',
      description: "Commune déléguée d'Annecy à forte dominante pavillonnaire. Les maisons individuelles construites entre les années 50 et 80, souvent avec cave, grenier et garage attenants, représentent les successions les plus volumineuses de l'agglomération. Les familles installées ici de longue date laissent des biens remplis d'un demi-siècle d'objets du quotidien."
    },
    {
      nom: 'Annecy-le-Vieux',
      description: "Villas cossues avec jardins sur les coteaux dominant le lac. Les successions y concernent souvent des propriétés de standing, avec des dépendances multiples (pool house, atelier, chai). Le tri préalable avec les héritiers est particulièrement important dans ce contexte, car des objets de valeur (tableaux, argenterie, mobilier d'époque) s'y mêlent aux encombrants."
    }
  ];

  const etapes = [
    { icon: Phone, step: '1', title: 'Premier échange', desc: 'Vous nous appelez ou un notaire nous contacte. Nous écoutons votre situation avec discrétion et planifions une visite au moment qui vous convient.' },
    { icon: ClipboardCheck, step: '2', title: 'Visite et inventaire', desc: 'Un responsable se rend sur place pour évaluer le volume, identifier les objets à conserver et rédiger un devis détaillé, gratuit et sans engagement.' },
    { icon: Users, step: '3', title: 'Tri avec les héritiers', desc: 'Avant toute évacuation, nous mettons de côté les objets personnels, souvenirs et documents que la famille souhaite récupérer. Rien ne part sans votre accord.' },
    { icon: Recycle, step: '4', title: 'Évacuation et valorisation', desc: 'Mobilier réutilisable orienté vers les associations locales, objets recyclables triés par matière. Chaque élément est traité dignement, rien n\'est jeté en vrac.' },
    { icon: Sparkles, step: '5', title: 'Remise en état du bien', desc: 'Le logement est vidé, balayé et prêt pour l\'estimation immobilière, la mise en vente ou la remise des clés aux héritiers.' }
  ];

  const avantages = [
    {
      title: 'Sensibilité et discrétion garanties',
      description: 'Notre équipe est formée à intervenir dans un contexte de deuil. Nous travaillons sans précipitation, en respectant le rythme des familles et la mémoire du défunt. Chaque objet est manipulé avec soin.'
    },
    {
      title: 'Coordination directe avec les notaires annéciens',
      description: 'Nous collaborons régulièrement avec les études notariales du centre-ville d\'Annecy (Balmettes, rue Royale, rue Sommeiller). Nous adaptons nos interventions au calendrier de la succession et fournissons les attestations nécessaires.'
    },
    {
      title: 'Connaissance approfondie du bâti annécien',
      description: 'Des maisons familiales de Cran-Gevrier aux appartements anciens de la Vieille Ville, nous connaissons chaque type de logement, ses accès et ses contraintes. Pas d\'improvisation le jour J.'
    },
    {
      title: 'Valorisation maximale avant évacuation',
      description: 'Avant de tout vider, nous identifions les objets ayant une valeur (mobilier ancien, livres rares, collections). Nous orientons les héritiers vers les brocanteurs et commissaires-priseurs de la région si nécessaire.'
    }
  ];

  const faq = [
    {
      question: 'Combien de temps après le décès peut-on faire débarrasser le logement à Annecy ?',
      answer: 'Il n\'y a pas de délai légal imposé, mais la plupart des familles nous contactent entre 1 et 3 mois après le décès, une fois les démarches notariales engagées. Si le logement est loué, le propriétaire peut demander la libération plus rapidement. Nous nous adaptons à chaque situation et pouvons intervenir sous 48 à 72 heures en cas d\'urgence.'
    },
    {
      question: 'Le notaire peut-il vous contacter directement pour organiser le débarras ?',
      answer: 'Oui, c\'est un cas fréquent à Annecy. Plusieurs études notariales du centre-ville nous recommandent à leurs clients. Le notaire nous communique les coordonnées des héritiers, et nous organisons la visite et le devis directement avec la famille. Cela simplifie la gestion pour des héritiers qui ne résident pas dans la région.'
    },
    {
      question: 'Que faites-vous des objets de valeur trouvés dans le logement (bijoux, tableaux, argenterie) ?',
      answer: 'Nous ne jetons jamais rien sans vérification. Tout objet potentiellement précieux est mis de côté et présenté aux héritiers. Si vous le souhaitez, nous pouvons vous orienter vers des commissaires-priseurs ou antiquaires de la région d\'Annecy pour une estimation. Notre rôle est de protéger les intérêts de la famille.'
    },
    {
      question: 'Les héritiers habitent loin d\'Annecy, comment procédez-vous ?',
      answer: 'C\'est très courant dans le bassin annécien, où beaucoup de biens sont détenus par des familles dont les enfants ont quitté la région. Nous pouvons effectuer la visite seuls (avec mandat ou en présence du notaire), envoyer des photos du contenu, et convenir par téléphone des objets à conserver. Nous expédions les souvenirs à l\'adresse de votre choix.'
    },
    {
      question: 'Intervenez-vous dans les maisons de Cran-Gevrier ou Annecy-le-Vieux avec dépendances multiples ?',
      answer: 'Absolument. Les pavillons de Cran-Gevrier et les villas d\'Annecy-le-Vieux comportent souvent cave, grenier, garage, abri de jardin et parfois un atelier. Nous vidons l\'ensemble en une seule intervention, tout compris dans le devis. C\'est le type de succession le plus volumineux et nous y sommes parfaitement rodés.'
    },
    {
      question: 'Quel est le coût d\'un débarras succession à Annecy ?',
      answer: 'Le prix varie selon le volume (nombre de pièces, dépendances), l\'accessibilité et la nature des objets. Pour un appartement T3 en succession, comptez entre 600 et 1 500 euros. Pour une maison avec dépendances à Cran-Gevrier ou Annecy-le-Vieux, entre 1 200 et 3 000 euros. Le devis est gratuit, sans engagement, et le prix annoncé est ferme.'
    }
  ];

  const communesVoisines = [
    { nom: 'Cran-Gevrier' },
    { nom: 'Seynod' },
    { nom: 'Meythet' },
    { nom: 'Annecy-le-Vieux' },
    { nom: 'Sevrier' },
    { nom: 'Saint-Jorioz' },
    { nom: 'Veyrier-du-Lac' },
    { nom: 'Menthon-Saint-Bernard' },
    { nom: 'Poisy' },
    { nom: 'Argonay' },
    { nom: 'Epagny-Metz-Tessy' },
    { nom: 'Pringy' },
    { nom: 'Rumilly' },
    { nom: 'Talloires-Montmin' }
  ];

  const jsonLdLocalBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://debeco.fr/#organization",
    "name": "DEBECO - Débarras & Recyclage",
    "description": "Débarras après succession à Annecy. Accompagnement respectueux des familles, coordination avec les notaires, tri des objets personnels, recyclage responsable en Haute-Savoie.",
    "url": "https://debeco.fr/debarras-succession-annecy",
    "telephone": "+33451401617",
    "email": "contact@debeco.fr",
    "image": "https://debeco.fr/images/succession.jpg",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4 Rue des Frères de Montgolfier",
      "addressLocality": "Annecy",
      "addressRegion": "Haute-Savoie",
      "postalCode": "74600",
      "addressCountry": "FR"
    },
    "areaServed": {
      "@type": "City",
      "name": "Annecy",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Haute-Savoie"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Débarras après succession",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Débarras succession à Annecy",
            "description": "Vidage complet de logement après décès ou succession à Annecy : tri des objets personnels, coordination notaire, recyclage responsable, remise en état."
          }
        }
      ]
    },
    "priceRange": "€€"
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://debeco.fr/services" },
      { "@type": "ListItem", "position": 3, "name": "Débarras succession", "item": "https://debeco.fr/services/succession" },
      { "@type": "ListItem", "position": 4, "name": "Annecy", "item": "https://debeco.fr/debarras-succession-annecy" }
    ]
  };

  const jsonLdFaq = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };

  useEffect(() => {
    const scripts = [jsonLdLocalBusiness, jsonLdBreadcrumb, jsonLdFaq].map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-managed', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => s.remove());
  }, []);

  return (
    <div className="min-h-screen">
      <SEO
        title="Débarras succession à Annecy (74) | DEBECO"
        description="Débarras après succession à Annecy : accompagnement respectueux, coordination notaire, devis gratuit. Appelez DEBECO au 04 51 40 16 17."
        canonical="/debarras-succession-annecy"
        ogImage="/images/succession.jpg"
      />

      {/* Breadcrumb */}
      <nav className="bg-gray-50 border-b" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <ol className="flex items-center text-sm text-gray-600 flex-wrap gap-y-1">
            <li><Link to="/" className="hover:text-emerald-600">Accueil</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services" className="hover:text-emerald-600">Services</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li><Link to="/services/succession" className="hover:text-emerald-600">Débarras succession</Link></li>
            <li><ChevronRight className="w-4 h-4 mx-2 text-gray-400" /></li>
            <li className="text-blue-900 font-medium">Annecy</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="local-h1">Débarras succession à Annecy</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
            Accompagnement respectueux pour vider un logement après un décès dans le bassin annécien. Coordination avec les notaires, tri des souvenirs, recyclage responsable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-devis">
                Demander un devis gratuit
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="hero-cta-phone">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Section: Notre service */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/succession.jpg"
                  webpSrc="/images/succession.webp"
                  alt="Intérieur d'un logement en succession à Annecy, objets personnels à trier avec respect avant débarras par DEBECO"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="eager"
                  data-testid="local-succession-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Heart className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Notre service de débarras succession à Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Annecy est une ville où les familles sont souvent implantées depuis plusieurs générations. Lorsqu'un proche disparaît, le logement qu'il occupait — parfois depuis des décennies — doit être vidé dans un contexte émotionnel difficile. C'est précisément pour ces situations que DEBECO a développé un accompagnement dédié aux successions.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Nous ne sommes pas de simples débarrasseurs. Avant toute évacuation, nous prenons le temps de trier avec les héritiers ou leur représentant : souvenirs de famille, documents importants, objets à valeur sentimentale ou marchande. Rien ne quitte le logement sans votre accord explicite.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Le bassin annécien attire de nombreux actifs qui ont quitté la région pour Genève, Lyon ou Paris. Leurs parents restent souvent dans la maison familiale de Cran-Gevrier, la villa d'Annecy-le-Vieux ou l'appartement de la Prairie. Quand la succession s'ouvre, les héritiers sont loin et ont besoin d'un prestataire de confiance sur place. C'est notre rôle.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Quartiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Les quartiers d'Annecy concernés par les successions</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
              Chaque secteur de l'agglomération annécienne a son propre profil de succession. Nous adaptons notre intervention au type de bien et au contexte familial.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quartiers.map((q, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <h3 className="font-bold text-lg text-blue-900">{q.nom}</h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">{q.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Section: Comment ca se passe */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Comment se déroule un débarras succession à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {etapes.map((e) => (
                <div key={e.step} className="text-center">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <e.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">Étape {e.step}</div>
                  <h3 className="font-bold text-blue-900 mb-2">{e.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{e.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pourquoi choisir DEBECO */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-10 text-center">Pourquoi confier une succession à DEBECO à Annecy ?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {avantages.map((a, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm border-2 border-transparent hover:border-emerald-500 transition-all">
                  <CheckCircle className="w-7 h-7 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-blue-900 mb-2">{a.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{a.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section: Zones desservies */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage
                  src="/images/annecy.jpg"
                  webpSrc="/images/annecy.webp"
                  alt="Vue panoramique du lac d'Annecy, zone d'intervention DEBECO pour le débarras succession"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="800"
                  loading="lazy"
                  data-testid="local-annecy-image"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Zones desservies autour d'Annecy</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Notre service de débarras succession couvre l'ensemble de l'agglomération annécienne. Les communes déléguées (Cran-Gevrier, Seynod, Meythet, Annecy-le-Vieux) et les communes voisines sont traitées aux mêmes conditions tarifaires.
                </p>
                <div className="flex flex-wrap gap-2">
                  {communesVoisines.map((c, index) => (
                    <span key={index} className="bg-blue-100 text-blue-900 px-3 py-1.5 rounded-full text-sm font-medium">
                      {c.nom}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <Link to="/zones/annecy" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    Voir toute notre zone d'intervention Annecy
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                  <Link to="/services/succession" className="text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center">
                    En savoir plus sur notre service succession
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <HelpCircle className="w-8 h-8 text-emerald-600" />
              <h2 className="text-3xl font-bold text-blue-900">Questions fréquentes — Succession</h2>
            </div>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <Card key={index} className="border-2 hover:border-emerald-500 transition-all">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg text-blue-900 mb-3">{item.question}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-white" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras à Annecy</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/debarras-maison-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Vide-maison complet, du grenier à la cave</p>
            </Link>
            <Link to="/debarras-appartement-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras d'appartement à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Intervention dans tous les immeubles, même sans ascenseur</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-gray-50 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Guide des prix de débarras à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Fourchettes tarifaires détaillées pour 2026</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras succession à Annecy ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez DEBECO pour un accompagnement respectueux et un devis gratuit. Nous intervenons dans tout le bassin annécien, en lien avec votre notaire si nécessaire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-devis-final">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto" data-testid="cta-phone-final">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DebarrasSuccessionAnnecy;
